// Formidlingsgebyr — reskontro over produsenter som har overtatt en forespørsel.
//
// Kunden skrev «Hvis de velger sistnevnte fakturerer vi de et
// formidlingsgebyr». Systemet lager ikke fakturaen: tre spørsmål er ubesvarte
// (fast sats? ved overtakelse eller etter levering? her eller i
// regnskapssystemet?), og et fakturaoppsett bygget før de svarene er like
// sannsynlig feil som riktig. Se backend/src/services/vendorFees.js.
//
// Det som er riktig uansett svar, er å vite hvem som skylder hva. Uten denne
// sida er gebyret et varsel i innboksen som noen enten husker eller ikke.
import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaidIcon from "@mui/icons-material/Paid";
import BlockIcon from "@mui/icons-material/Block";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

import { listVendorFees, updateVendorFee } from "api/apiService";
import { notifyCenter } from "utils/toast";

// Soft UI tvinger display:grid + placeItems:center på MuiInputBase-root, som
// krymper klikkflaten. Samme overstyring som i jobblista.
const SELECT_SX = {
  height: 40,
  "&.MuiInputBase-root": {
    display: "flex !important",
    placeItems: "initial !important",
    padding: "0 !important",
  },
  "& .MuiSelect-select": {
    display: "flex !important",
    alignItems: "center !important",
    minHeight: "40px !important",
    boxSizing: "border-box",
    padding: "0 32px 0 14px !important",
    flex: 1,
    width: "100% !important",
  },
};

const STATUS = {
  pending: { label: "Ikke fakturert", color: "warning" },
  invoiced: { label: "Fakturert", color: "info" },
  paid: { label: "Betalt", color: "success" },
  waived: { label: "Frafalt", color: "default" },
};

const nok = (n) =>
  `${Number(n || 0).toLocaleString("nb-NO", { maximumFractionDigits: 2 })} kr`;

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("nb-NO");
};

/** Én av de fire summene øverst. */
function Tile({ label, value, color }) {
  return (
    <Grid item xs={6} md={3}>
      <Card sx={{ p: 2 }}>
        <SoftTypography variant="caption" color="text">
          {label}
        </SoftTypography>
        <SoftTypography variant="h5" fontWeight="bold" sx={{ color }}>
          {value}
        </SoftTypography>
      </Card>
    </Grid>
  );
}

Tile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

function VendorFees() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [vendorId, setVendorId] = useState("");
  // Produsentene i filteret huskes fra siste ufiltrerte svar. Ellers ville
  // lista krympet til én produsent så snart man valgte én.
  const [vendorOptions, setVendorOptions] = useState([]);

  // Dialogen dekker begge overgangene som krever at admin skriver noe:
  // fakturanummer ved «fakturert», begrunnelse ved «frafalt».
  const [dialog, setDialog] = useState(null); // { fee, next }
  const [invoiceRef, setInvoiceRef] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listVendorFees({ status, vendorId, from, to });
      if (res?.ok) {
        setData(res);
        if (!vendorId) {
          setVendorOptions((res.by_vendor || []).map((v) => ({ id: v.vendor_id, name: v.vendor_name })));
        }
      }
      else notifyCenter("error", "Kunne ikke hente gebyrene.");
    } catch (e) {
      notifyCenter("error", "Kunne ikke hente gebyrene.");
    } finally {
      setLoading(false);
    }
  }, [status, vendorId, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const fees = useMemo(() => data?.fees || [], [data]);
  const totals = data?.totals || {};

  const openDialog = (fee, next) => {
    setInvoiceRef(fee.invoice_ref || "");
    setNote(fee.note || "");
    setDialog({ fee, next });
  };

  const apply = async (fee, next, extra = {}) => {
    setBusy(true);
    try {
      const res = await updateVendorFee(fee.id, next, extra);
      if (res?.ok) {
        notifyCenter("success", `Gebyret er satt til «${STATUS[next].label.toLowerCase()}».`);
        setDialog(null);
        await load();
      } else {
        const msg = {
          invoice_ref_required: "Fakturanummer må fylles ut.",
          note_required: "Skriv en begrunnelse for frafallet.",
          not_a_takeover: "Denne raden er ikke en overtakelse.",
        }[res?.error];
        notifyCenter("error", msg || "Kunne ikke oppdatere gebyret.");
      }
    } finally {
      setBusy(false);
    }
  };

  /** CSV med semikolon og komma-desimal — det Excel på norsk locale forventer. */
  const exportCsv = () => {
    const head = [
      "Ordre", "Byggeplass", "Produsent", "E-post", "Beløp eks. mva",
      "Status", "Fakturanr", "Overtatt", "Fakturert", "Betalt", "Notat",
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = fees.map((f) =>
      [
        f.order_id,
        f.site,
        f.vendor_name,
        f.vendor_email || "",
        String(f.amount).replace(".", ","),
        STATUS[f.status]?.label || f.status,
        f.invoice_ref || "",
        fmt(f.taken_over_at),
        fmt(f.invoiced_at),
        fmt(f.paid_at),
        f.note || "",
      ].map(esc).join(";")
    );
    // ﻿ er BOM: uten den leser Excel æ, ø og å som skrot.
    const blob = new Blob([`﻿${[head.map(esc).join(";"), ...rows].join("\r\n")}`], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `formidlingsgebyr-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <DashboardLayout>
      <SoftBox py={3}>
        <SoftBox mb={2}>
          <SoftTypography variant="h5" fontWeight="bold">
            Formidlingsgebyr
          </SoftTypography>
          <SoftTypography variant="caption" color="text">
            Produsenter som har overtatt en forespørsel og skal faktureres. Fakturaen lages i
            regnskapssystemet — her fører du inn fakturanummeret og holder oversikt over hva som
            er krevd inn.
          </SoftTypography>
        </SoftBox>

        {/* ------------------------------- summer ------------------------------ */}
        <Grid container spacing={2} mb={2}>
          <Tile label="Utestående" value={nok(totals.outstanding)} color="#b26a00" />
          <Tile label="Ikke fakturert" value={nok(totals.pending)} color="#b26a00" />
          <Tile label="Fakturert" value={nok(totals.invoiced)} color="#1d4ed8" />
          <Tile label="Betalt" value={nok(totals.paid)} color="#0f8a43" />
        </Grid>

        {/* ---------------------------- per produsent -------------------------- */}
        {/* Månedlig fakturering per produsent (s. 6 pkt. 8): velg måneden i
            datofilteret, så står det én linje per produsent å fakturere. */}
        {!vendorId && (data?.by_vendor || []).length > 0 && (
          <Card sx={{ p: 3, mb: 2 }}>
            <SoftTypography variant="button" fontWeight="bold" display="block" mb={1}>
              Per produsent{from || to ? " i valgt periode" : ""}
            </SoftTypography>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    {["Produsent", "Overtakelser", "Ikke fakturert", "Utestående", "Sum"].map((h) => (
                      <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: "#67748e" }}>
                        {h.toUpperCase()}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.by_vendor.map((v) => (
                    <TableRow
                      key={v.vendor_id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => setVendorId(v.vendor_id)}
                    >
                      <TableCell sx={{ fontSize: 13, fontWeight: 700 }}>{v.vendor_name}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{v.count}</TableCell>
                      <TableCell sx={{ fontSize: 13, color: "#b26a00", fontWeight: 700 }}>
                        {nok(v.pending)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{nok(v.outstanding)}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{nok(v.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}

        <Card sx={{ p: 3 }}>
          {/* ------------------------------ filtre ----------------------------- */}
          <Grid container spacing={2} alignItems="flex-end" mb={2}>
            <Grid item xs={12} sm={3}>
              <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                Produsent
              </SoftTypography>
              <FormControl fullWidth>
                <Select
                  value={vendorId}
                  onChange={(e) => setVendorId(e.target.value)}
                  displayEmpty
                  sx={SELECT_SX}
                >
                  <MenuItem value="">Alle</MenuItem>
                  {vendorOptions.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                Status
              </SoftTypography>
              <FormControl fullWidth>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  displayEmpty
                  sx={SELECT_SX}
                >
                  <MenuItem value="">Alle</MenuItem>
                  {Object.entries(STATUS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>
                      {v.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                Overtatt fra
              </SoftTypography>
              <SoftInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                til
              </SoftTypography>
              <SoftInput type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <SoftBox display="flex" gap={1}>
                <SoftButton size="small" variant="outlined" color="secondary" onClick={load}>
                  <RefreshIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  Oppdater
                </SoftButton>
                <SoftButton
                  size="small"
                  variant="outlined"
                  color="info"
                  onClick={exportCsv}
                  disabled={!fees.length}
                >
                  <DownloadIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  CSV
                </SoftButton>
              </SoftBox>
            </Grid>
          </Grid>

          {/* ------------------------------ tabellen --------------------------- */}
          {loading ? (
            <SoftBox display="flex" justifyContent="center" py={4}>
              <CircularProgress size={24} />
            </SoftBox>
          ) : !fees.length ? (
            <SoftBox py={4} textAlign="center">
              <SoftTypography variant="body2" color="text">
                Ingen overtatte forespørsler{status || vendorId || from || to ? " som matcher filteret" : " ennå"}.
              </SoftTypography>
            </SoftBox>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    {["Ordre", "Produsent", "Beløp", "Overtatt", "Status", "Fakturanr", ""].map((h) => (
                      <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: "#67748e" }}>
                        {h.toUpperCase()}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fees.map((f) => (
                    <TableRow key={f.id} hover>
                      <TableCell>
                        <SoftTypography variant="button" fontWeight="bold">
                          #{f.order_id}
                        </SoftTypography>
                        <SoftTypography variant="caption" color="text" display="block">
                          {f.site || "—"}
                        </SoftTypography>
                      </TableCell>
                      <TableCell>
                        <SoftTypography variant="button">{f.vendor_name}</SoftTypography>
                        <SoftTypography variant="caption" color="text" display="block">
                          {f.vendor_email || "ingen e-post"}
                        </SoftTypography>
                      </TableCell>
                      <TableCell>
                        <SoftTypography variant="button" fontWeight="bold">
                          {nok(f.amount)}
                        </SoftTypography>
                        <SoftTypography variant="caption" color="text" display="block">
                          eks. mva.
                        </SoftTypography>
                      </TableCell>
                      <TableCell>
                        <SoftTypography variant="caption">{fmt(f.taken_over_at)}</SoftTypography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={f.note || ""} disableHoverListener={!f.note}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={STATUS[f.status]?.label || f.status}
                            color={STATUS[f.status]?.color || "default"}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <SoftTypography variant="caption">{f.invoice_ref || "—"}</SoftTypography>
                      </TableCell>
                      <TableCell align="right">
                        <SoftBox display="flex" gap={0.5} justifyContent="flex-end">
                          {f.status === "pending" && (
                            <>
                              <SoftButton
                                size="small"
                                color="info"
                                variant="gradient"
                                disabled={busy}
                                onClick={() => openDialog(f, "invoiced")}
                              >
                                <ReceiptLongIcon sx={{ fontSize: 15, mr: 0.5 }} />
                                Fakturert
                              </SoftButton>
                              <SoftButton
                                size="small"
                                color="secondary"
                                variant="outlined"
                                disabled={busy}
                                onClick={() => openDialog(f, "waived")}
                              >
                                <BlockIcon sx={{ fontSize: 15 }} />
                              </SoftButton>
                            </>
                          )}
                          {f.status === "invoiced" && (
                            <SoftButton
                              size="small"
                              color="success"
                              variant="gradient"
                              disabled={busy}
                              onClick={() => apply(f, "paid")}
                            >
                              <PaidIcon sx={{ fontSize: 15, mr: 0.5 }} />
                              Betalt
                            </SoftButton>
                          )}
                          {(f.status === "paid" || f.status === "waived") && (
                            <SoftButton
                              size="small"
                              color="secondary"
                              variant="text"
                              disabled={busy}
                              onClick={() => apply(f, "pending")}
                            >
                              Angre
                            </SoftButton>
                          )}
                        </SoftBox>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </SoftBox>

      {/* --------------------------------- dialog -------------------------------- */}
      <Dialog open={!!dialog} onClose={() => !busy && setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 18, fontWeight: 700 }}>
          {dialog?.next === "invoiced" ? "Marker som fakturert" : "Frafall gebyret"}
        </DialogTitle>
        <DialogContent>
          <SoftTypography variant="caption" color="text" display="block" mb={2}>
            {dialog?.fee?.vendor_name} — {nok(dialog?.fee?.amount)} eks. mva. på ordre #
            {dialog?.fee?.order_id}
          </SoftTypography>

          {dialog?.next === "invoiced" ? (
            <>
              <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                Fakturanummer
              </SoftTypography>
              <SoftInput
                value={invoiceRef}
                placeholder="F.eks. 2026-1043"
                onChange={(e) => setInvoiceRef(e.target.value)}
              />
              <SoftTypography variant="caption" color="text" display="block" mt={1}>
                Nummeret fra regnskapssystemet, så raden kan spores tilbake dit.
              </SoftTypography>
            </>
          ) : (
            <>
              <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                Begrunnelse
              </SoftTypography>
              <SoftInput
                multiline
                rows={3}
                value={note}
                placeholder="Hvorfor kreves ikke gebyret inn?"
                onChange={(e) => setNote(e.target.value)}
              />
            </>
          )}

          <SoftBox mt={3} display="flex" gap={1} justifyContent="flex-end">
            <SoftButton size="small" variant="text" color="secondary" onClick={() => setDialog(null)}>
              Avbryt
            </SoftButton>
            <SoftButton
              size="small"
              color={dialog?.next === "invoiced" ? "info" : "secondary"}
              variant="gradient"
              disabled={
                busy ||
                (dialog?.next === "invoiced" ? !invoiceRef.trim() : !note.trim())
              }
              onClick={() =>
                apply(dialog.fee, dialog.next, { invoiceRef, note })
              }
            >
              {busy ? "Lagrer…" : "Lagre"}
            </SoftButton>
          </SoftBox>
        </DialogContent>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default VendorFees;
